import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { t } from '../i18n';
import { ReaderSettings } from '../components/ReaderSettings';
import { api, ApiError } from '../lib/api';
import { useSettings } from '../lib/queries';
import { useAuth } from '../lib/auth';
import { fmtDateTime, fmtRelative } from '../lib/format';
import type { DocumentType, ManagedUser, MorningStatus, Permission, Settings } from '../lib/types';
import { Button, Callout, Checkbox, ErrorState, Field, Input, Tabs, Textarea, useConfirm, useToast } from '../components/ui';
import { ArchiveSettings } from './Archive';

type Tab = 'users' | 'rules' | 'business' | 'account' | 'backups' | 'audit';
const ADMIN_TABS: Tab[] = ['users', 'rules', 'business', 'backups', 'audit'];

export function SettingsPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const initial = new URLSearchParams(window.location.search).get('tab') as Tab | null;
  const allowed = (k: Tab | null): k is Tab => Boolean(k && (isAdmin || !ADMIN_TABS.includes(k)) && ['users', 'rules', 'business', 'account', 'backups', 'audit'].includes(k));
  const [tab, setTab] = useState<Tab>(allowed(initial) ? initial : isAdmin ? 'rules' : 'account');
  const pending = useQuery({ queryKey: ['users', 'pending'], queryFn: () => api<{ count: number }>('/api/users/pending-count'), enabled: isAdmin });
  const tabs: Array<{ key: Tab; label: string; count?: number }> = isAdmin
    ? [
        { key: 'users', label: 'משתמשים והרשאות', count: pending.data?.count || undefined },
        { key: 'rules', label: 'כללי זיהוי מסמכים' },
        { key: 'business', label: 'פרטי העסק ומורנינג' },
        { key: 'account', label: 'החשבון שלי' },
        { key: 'backups', label: 'גיבויים' },
        { key: 'audit', label: 'יומן אבטחה' },
      ]
    : [{ key: 'account', label: 'החשבון שלי' }];
  return (
    <>
      <div className="page-head">
        <div>
          <h1>{t.nav.settings}</h1>
          <div className="sub">{isAdmin ? 'משתמשים והרשאות, כללי הזיהוי של סוכן המייל, החשבון שלך ויומן האבטחה.' : 'פרטי החשבון שלך והחלפת סיסמה.'}</div>
        </div>
      </div>
      <Tabs<Tab> value={tab} onChange={setTab} tabs={tabs} />
      {tab === 'users' ? <Users /> : tab === 'rules' ? <Rules /> : tab === 'business' ? <Business /> : tab === 'account' ? <Account /> : tab === 'backups' ? <Backups /> : <Audit />}
    </>
  );
}

type UsersResponse = { items: ManagedUser[]; permissions: Record<Permission, string>; defaultPermissions: Permission[] };

function Users() {
  const { user: me } = useAuth();
  const { data, error } = useQuery({ queryKey: ['users'], queryFn: () => api<UsersResponse>('/api/users') });
  if (error) return <div className="panel"><ErrorState error={error} /></div>;
  if (!data) return <div className="panel panel-body"><span className="skeleton" style={{ width: '40%' }} /></div>;
  const pending = data.items.filter((u) => u.status === 'pending');
  const others = data.items.filter((u) => u.status !== 'pending');
  return (
    <div className="grid-main-side">
      <div className="stack">
        {pending.length ? (
          <section className="panel">
            <div className="panel-head">
              <h2>בקשות הרשמה</h2>
              <span className="badge warn">{pending.length}</span>
            </div>
            {pending.map((u) => <PendingUser key={u.id} u={u} data={data} />)}
          </section>
        ) : null}
        <section className="panel">
          <div className="panel-head"><h2>משתמשים</h2></div>
          {others.map((u) => <UserRow key={u.id} u={u} data={data} isMe={u.id === me?.id} />)}
        </section>
      </div>
      <aside className="stack">
        <Callout tone="info" title="איך זה עובד">
          <ul style={{ margin: '4px 0 0', paddingInlineStart: 18 }} className="small">
            <li>עובדים נרשמים ממסך הכניסה ("הרשמה למערכת") ויכולים להתחבר רק אחרי אישור שלך.</li>
            <li><strong>מנהל</strong> — גישה מלאה, כולל הגדרות, משתמשים, חיבורי מייל ומורנינג וגיבויים.</li>
            <li><strong>עובד</strong> — צופה בלקוחות ובמסמכים, ומבצע רק את מה שסימנת לו. ההרשאות נאכפות בשרת, לא רק בתצוגה.</li>
            <li>השבתה מנתקת את המשתמש מיד. שינוי הרשאות חל מהפעולה הבאה שלו.</li>
          </ul>
        </Callout>
      </aside>
    </div>
  );
}

function PermissionBoxes({ value, onChange, labels, disabled }: { value: Permission[]; onChange: (v: Permission[]) => void; labels: Record<Permission, string>; disabled?: boolean }) {
  return (
    <div className="stack" style={{ gap: 6 }}>
      {(Object.keys(labels) as Permission[]).map((p) => (
        <Checkbox key={p} checked={value.includes(p)} disabled={disabled} onChange={(on) => onChange(on ? [...value, p] : value.filter((x) => x !== p))}>
          <span className="small">{labels[p]}</span>
        </Checkbox>
      ))}
    </div>
  );
}

function PendingUser({ u, data }: { u: ManagedUser; data: UsersResponse }) {
  const [role, setRole] = useState<'member' | 'admin'>('member');
  const [perms, setPerms] = useState<Permission[]>(data.defaultPermissions);
  const [busy, setBusy] = useState<string | null>(null);
  const qc = useQueryClient();
  const toast = useToast();
  const confirm = useConfirm();
  const refresh = () => qc.invalidateQueries({ queryKey: ['users'] });
  const approve = async () => {
    setBusy('approve');
    try {
      await api(`/api/users/${u.id}/approve`, { body: { role, permissions: perms } });
      toast.success(`${u.name} אושר/ה — אפשר להתחבר`);
      refresh();
    } catch (e) {
      toast.error(e);
    } finally {
      setBusy(null);
    }
  };
  const reject = async () => {
    if (!(await confirm({ title: `לדחות את בקשת ההרשמה של ${u.name}?`, body: 'הבקשה תימחק. אפשר להירשם מחדש בהמשך.', confirmLabel: 'דחייה', danger: true }))) return;
    setBusy('reject');
    try {
      await api(`/api/users/${u.id}`, { method: 'DELETE' });
      toast.success('הבקשה נדחתה');
      refresh();
    } catch (e) {
      toast.error(e);
    } finally {
      setBusy(null);
    }
  };
  return (
    <div className="panel-body stack" style={{ borderBottom: '1px solid var(--line)' }}>
      <div className="row wrap">
        <strong>{u.name}</strong>
        <bdi className="ltr small muted">{u.email}</bdi>
        <span className="small muted">נרשם/ה {fmtRelative(u.createdAt)}</span>
      </div>
      <div className="row wrap" style={{ gap: 16 }}>
        <label className="row small">
          <input type="radio" checked={role === 'member'} onChange={() => setRole('member')} /> עובד
        </label>
        <label className="row small">
          <input type="radio" checked={role === 'admin'} onChange={() => setRole('admin')} /> מנהל (גישה מלאה)
        </label>
      </div>
      {role === 'member' ? <PermissionBoxes value={perms} onChange={setPerms} labels={data.permissions} /> : null}
      <div className="row">
        <Button variant="primary" size="sm" onClick={approve} loading={busy === 'approve'} disabled={busy !== null}>אישור</Button>
        <Button size="sm" variant="ghost" onClick={reject} loading={busy === 'reject'} disabled={busy !== null}>דחייה</Button>
      </div>
    </div>
  );
}

function UserRow({ u, data, isMe }: { u: ManagedUser; data: UsersResponse; isMe: boolean }) {
  const [perms, setPerms] = useState<Permission[]>(u.permissions);
  const [busy, setBusy] = useState(false);
  const qc = useQueryClient();
  const toast = useToast();
  const confirm = useConfirm();
  const dirty = [...perms].sort().join() !== [...u.permissions].sort().join();
  const patch = async (body: Record<string, unknown>, ok: string) => {
    setBusy(true);
    try {
      await api(`/api/users/${u.id}`, { method: 'PATCH', body });
      toast.success(ok);
      qc.invalidateQueries({ queryKey: ['users'] });
    } catch (e) {
      toast.error(e);
    } finally {
      setBusy(false);
    }
  };
  const toggleStatus = async () => {
    if (u.status === 'active' && !(await confirm({ title: `להשבית את ${u.name}?`, body: 'המשתמש יתנתק מיד ולא יוכל להתחבר עד שיופעל מחדש.', confirmLabel: 'השבתה', danger: true }))) return;
    await patch({ status: u.status === 'active' ? 'disabled' : 'active' }, u.status === 'active' ? 'המשתמש הושבת' : 'המשתמש הופעל');
  };
  return (
    <div className="panel-body stack" style={{ borderBottom: '1px solid var(--line)', opacity: u.status === 'disabled' ? 0.65 : 1 }}>
      <div className="row wrap">
        <strong>{u.name}</strong>
        <bdi className="ltr small muted">{u.email}</bdi>
        <span className={`badge ${u.role === 'admin' ? 'accent' : ''}`}>{u.role === 'admin' ? 'מנהל' : 'עובד'}</span>
        {u.status === 'disabled' ? <span className="badge bad">מושבת</span> : null}
        {isMe ? <span className="small muted">(המשתמש שלך)</span> : null}
        <span className="spacer" />
        <span className="small muted">{u.lastLoginAt ? `התחבר/ה ${fmtRelative(u.lastLoginAt)}` : 'טרם התחבר/ה'}</span>
      </div>
      {!isMe ? (
        <>
          {u.role === 'member' ? (
            <PermissionBoxes value={perms} onChange={setPerms} labels={data.permissions} disabled={u.status === 'disabled'} />
          ) : (
            <p className="small muted">למנהל יש גישה מלאה לכל המערכת.</p>
          )}
          <div className="row wrap">
            {u.role === 'member' && dirty ? (
              <Button size="sm" variant="primary" onClick={() => patch({ permissions: perms }, 'ההרשאות עודכנו')} loading={busy}>שמירת הרשאות</Button>
            ) : null}
            <Button size="sm" onClick={() => patch({ role: u.role === 'admin' ? 'member' : 'admin' }, 'התפקיד עודכן')} disabled={busy || u.status === 'disabled'}>
              {u.role === 'admin' ? 'הפיכה לעובד' : 'הפיכה למנהל'}
            </Button>
            <Button size="sm" variant="ghost" onClick={toggleStatus} disabled={busy}>{u.status === 'active' ? 'השבתה' : 'הפעלה מחדש'}</Button>
          </div>
        </>
      ) : (
        <p className="small muted">את התפקיד וההרשאות של המשתמש שלך אי אפשר לשנות מכאן — כך המערכת לעולם לא נשארת בלי מנהל.</p>
      )}
    </div>
  );
}

const lines = (v: string) => v.split(/[\n,]/).map((s) => s.trim()).filter(Boolean);

function Business() {
  const { data, error } = useSettings();
  const [s, setS] = useState<Settings | null>(null);
  const [busy, setBusy] = useState(false);
  const toast = useToast();
  const qc = useQueryClient();
  useEffect(() => {
    if (data) setS(data.settings);
  }, [data]);
  if (error) return <div className="panel"><ErrorState error={error} /></div>;
  if (!s) return <div className="panel panel-body"><span className="skeleton" style={{ width: '40%' }} /></div>;
  const bp = s.businessProfile;
  const setBp = (patch: Partial<Settings['businessProfile']>) => setS({ ...s, businessProfile: { ...bp, ...patch } });
  const save = async () => {
    setBusy(true);
    try {
      await api('/api/settings', { method: 'PUT', body: s });
      qc.invalidateQueries({ queryKey: ['settings'] });
      toast.success(t.common.savedToast);
    } catch (e) {
      toast.error(e);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="grid-main-side">
      <div className="stack">
        <div className="panel panel-body">
          <div className="form-section">
            <div className="form-section-title">פרטי העסק — מופיעים בכרטסת הלקוח</div>
            <div className="form-grid">
              <Field label="שם העסק" required>
                <Input value={bp.name} onChange={(e) => setBp({ name: e.target.value })} />
              </Field>
              <Field label="ע.מ / ח.פ">
                <Input value={bp.companyNumber} onChange={(e) => setBp({ companyNumber: e.target.value })} ltr />
              </Field>
              <Field label="טלפון">
                <Input value={bp.phone} onChange={(e) => setBp({ phone: e.target.value })} ltr />
              </Field>
              <Field label="אימייל">
                <Input value={bp.email} onChange={(e) => setBp({ email: e.target.value })} ltr />
              </Field>
              <Field label="כתובת" className="span-2">
                <Input value={bp.address} onChange={(e) => setBp({ address: e.target.value })} />
              </Field>
            </div>
          </div>
          <div className="form-section">
            <div className="form-section-title">מעקב הזמנות</div>
            <div className="form-grid">
              <Field label="אחרי פנייה ללקוח — להסתיר אותו מהמעקב למשך (ימים)" hint="או עד שיזמין שוב, המוקדם מביניהם">
                <Input type="number" min={1} max={90} value={s.followUpCooldownDays} onChange={(e) => setS({ ...s, followUpCooldownDays: Math.min(90, Math.max(1, Number(e.target.value) || 14)) })} ltr />
              </Field>
            </div>
          </div>
          <div className="form-section">
            <div className="form-section-title">רווחיות</div>
            <div className="form-grid">
              <Field label="יעד רווחיות (%)" hint="רווח גולמי מהמכירה: (מחיר − עלות) ÷ מחיר. מעל היעד — ירוק">
                <Input type="number" min={1} max={95} value={s.profit.target} onChange={(e) => setS({ ...s, profit: { ...s.profit, target: Math.min(95, Math.max(1, Number(e.target.value) || 30)) } })} ltr />
              </Field>
              <Field label="רווחיות נמוכה מתחת ל- (%)" hint="בין זה ליעד — צהוב; מתחת — אדום; מתחת לעלות — אדום כהה">
                <Input type="number" min={0} max={95} value={s.profit.warn} onChange={(e) => setS({ ...s, profit: { ...s.profit, warn: Math.min(95, Math.max(0, Number(e.target.value) || 0)) } })} ltr />
              </Field>
            </div>
          </div>
          <div className="form-section">
            <div className="form-section-title">הזמנות</div>
            <div className="form-grid">
              <Field label="שורת משלוח — תיאור" hint="נוספת בסימון אחד בעורך ההזמנות">
                <Input value={s.delivery.label} onChange={(e) => setS({ ...s, delivery: { ...s.delivery, label: e.target.value } })} />
              </Field>
              <Field label="מחיר משלוח (לפני מע״מ)">
                <Input inputMode="decimal" value={String(s.delivery.price)} onChange={(e) => setS({ ...s, delivery: { ...s.delivery, price: Math.max(0, Number(e.target.value.replace(/[₪,\s]/g, '')) || 0) } })} ltr />
              </Field>
            </div>
          </div>
          <div className="row">
            <Button variant="primary" onClick={save} loading={busy} disabled={!bp.name.trim()}>{t.common.save}</Button>
          </div>
        </div>
        <Morning />
        <ReaderSettings />
      </div>
      <aside className="stack">
        <Callout tone="info" title="חיבור ישיר למורנינג">
          <p className="small">החיבור הישיר (API) משמש את סוכן מורנינג למשיכת מסמכים וסטטוסים (המייל משמש לאימות), ואת הפקת המסמכים מתוך הזמנות. ההפקה מופעלת בנפרד — קודם ב-Sandbox, ורק אחר כך אמיתית.</p>
          <p className="small" style={{ marginTop: 6 }}>מפתח API יוצרים במורנינג: <strong>הגדרות ← כלים למפתחים ← API</strong> (Client ID + Client Secret). הסוד נשמר מוצפן ואינו מוצג שוב. גישת API זמינה רק במנויים שכוללים אותה.</p>
        </Callout>
      </aside>
    </div>
  );
}

function Morning() {
  const { user } = useAuth();
  const status = useQuery({ queryKey: ['morning'], queryFn: () => api<MorningStatus>('/api/integrations/morning') });
  const [apiKeyId, setApiKeyId] = useState('');
  const [secret, setSecret] = useState('');
  const [environment, setEnvironment] = useState<'production' | 'sandbox'>('production');
  const [busy, setBusy] = useState<string | null>(null);
  const toast = useToast();
  const qc = useQueryClient();
  const run = async (key: string, fn: () => Promise<unknown>, ok: string) => {
    setBusy(key);
    try {
      await fn();
      qc.invalidateQueries({ queryKey: ['morning'] });
      toast.success(ok);
      setSecret('');
    } catch (e) {
      toast.error(e);
    } finally {
      setBusy(null);
    }
  };
  const s = status.data;
  const isAdmin = user?.role === 'admin';
  return (
    <div className="panel panel-body">
      <div className="form-section">
        <div className="form-section-title">מורנינג (חשבונית ירוקה) — חיבור API</div>
        {s?.connected ? (
          <div className="stack">
            <dl className="kv">
              <dt>סטטוס</dt>
              <dd>{s.lastTestOk === false ? <span className="badge bad">החיבור נכשל</span> : <span className="badge ok">מחובר</span>}{s.documentsFound != null ? ` · ${s.documentsFound.toLocaleString('he-IL')} מסמכים בחשבון` : ''}</dd>
              <dt>מפתח</dt>
              <dd><bdi className="ltr">{s.apiKeyId}</bdi> · {s.environment === 'sandbox' ? 'סביבת בדיקות' : 'סביבה אמיתית'}</dd>
              <dt>בדיקה אחרונה</dt>
              <dd>{s.lastTestAt ? fmtDateTime(s.lastTestAt) : '—'}{s.lastError ? <span className="small" style={{ color: 'var(--bad)' }}> · {s.lastError}</span> : null}</dd>
            </dl>
            <div className="row">
              <Button onClick={() => run('test', () => api('/api/integrations/morning/test', { body: {} }), 'הבדיקה הסתיימה')} loading={busy === 'test'} disabled={!isAdmin}>בדיקת חיבור</Button>
              <Button variant="ghost" onClick={() => run('off', () => api('/api/integrations/morning', { method: 'DELETE' }), 'החיבור הוסר')} loading={busy === 'off'} disabled={!isAdmin}>ניתוק</Button>
            </div>
          </div>
        ) : (
          <div className="stack">
            <div className="form-grid">
              <Field label="Client ID" required>
                <Input value={apiKeyId} onChange={(e) => setApiKeyId(e.target.value)} ltr autoComplete="off" />
              </Field>
              <Field label="Client Secret" required>
                <Input type="password" value={secret} onChange={(e) => setSecret(e.target.value)} ltr autoComplete="new-password" />
              </Field>
              <Field label="סביבה">
                <select className="select" value={environment} onChange={(e) => setEnvironment(e.target.value as 'production' | 'sandbox')}>
                  <option value="production">אמיתית</option>
                  <option value="sandbox">בדיקות (Sandbox)</option>
                </select>
              </Field>
            </div>
            <div className="row">
              <Button
                variant="primary"
                onClick={() => run('connect', () => api('/api/integrations/morning', { method: 'PUT', body: { apiKeyId: apiKeyId.trim(), secret: secret.trim(), environment } }), 'מורנינג חוברה')}
                loading={busy === 'connect'}
                disabled={!isAdmin || apiKeyId.trim().length < 8 || secret.trim().length < 8}
              >
                חיבור ובדיקה
              </Button>
              {!isAdmin ? <span className="small muted">למנהל בלבד</span> : null}
            </div>
          </div>
        )}
      </div>
      <IssueSettings />
      {isAdmin ? <ArchiveSettings /> : null}
    </div>
  );
}

type IssueStatus = {
  target: 'off' | 'sandbox' | 'production';
  sandbox: { connected: boolean; apiKeyId?: string; connectedAt?: string; lastTestOk?: boolean; lastError?: string | null };
  production: { connected: boolean; environment: 'production' | 'sandbox' | null };
};

/** Issuing documents from the CRM: off / Morning Sandbox (test documents) / the real business. */
function IssueSettings() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const status = useQuery({ queryKey: ['morning-issue'], queryFn: () => api<IssueStatus>('/api/integrations/morning/issue') });
  const [apiKeyId, setApiKeyId] = useState('');
  const [secret, setSecret] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  const toast = useToast();
  const confirm = useConfirm();
  const qc = useQueryClient();
  const run = async (key: string, fn: () => Promise<unknown>, ok: string) => {
    setBusy(key);
    try {
      await fn();
      qc.invalidateQueries({ queryKey: ['morning-issue'] });
      toast.success(ok);
      setSecret('');
      setApiKeyId('');
    } catch (e) {
      toast.error(e);
    } finally {
      setBusy(null);
    }
  };
  const s = status.data;
  if (!s) return null;
  const setTarget = async (target: IssueStatus['target']) => {
    if (target === 'production') {
      const ok = await confirm({
        title: 'להפעיל הפקה של מסמכים אמיתיים?',
        body: 'מעכשיו "אישור והפקה" בהזמנה יפיק מסמך אמיתי במורנינג ויישלח ללקוח. חשבונית מס אינה ניתנת למחיקה — רק לביטול בחשבונית זיכוי. מומלץ לבדוק קודם ב-Sandbox.',
        confirmLabel: 'הפעלה',
        danger: true,
      });
      if (!ok) return;
    }
    await run('target', () => api('/api/integrations/morning/issue', { method: 'PUT', body: { target } }), 'נשמר');
  };
  const TARGETS: Array<{ key: IssueStatus['target']; label: string; hint: string; disabled: boolean }> = [
    { key: 'off', label: 'כבויה', hint: 'אפשר להקליד הזמנות ולראות תצוגה מקדימה בלבד', disabled: false },
    { key: 'sandbox', label: 'בדיקות (Sandbox)', hint: s.sandbox.connected ? 'מסמכי בדיקה — לא אמיתיים, לא נכנסים להנהלת החשבונות' : 'קודם מחברים מפתחות Sandbox למטה', disabled: !s.sandbox.connected },
    { key: 'production', label: 'אמיתית', hint: s.production.connected && s.production.environment === 'production' ? 'מסמכים אמיתיים מחשבון מורנינג המחובר' : 'דורש את החיבור הראשי לחשבון האמיתי', disabled: !(s.production.connected && s.production.environment === 'production') },
  ];
  return (
    <div className="form-section">
      <div className="form-section-title">הפקת מסמכים מהמערכת</div>
      <div className="stack">
        <div className="seg" role="radiogroup" aria-label="הפקה אל">
          {TARGETS.map((t) => (
            <button key={t.key} type="button" role="radio" aria-checked={s.target === t.key} className={`seg-btn ${s.target === t.key ? 'active' : ''}`} disabled={!isAdmin || t.disabled || busy !== null} onClick={() => setTarget(t.key)} title={t.hint}>
              {t.label}
            </button>
          ))}
        </div>
        <div className="small muted">{TARGETS.find((t) => t.key === s.target)?.hint}</div>
        {s.target === 'production' ? <Callout tone="warn">הפקה אמיתית פעילה: מסמכים שמופקים מהמערכת הם מסמכים רשמיים ונשלחים ללקוח.</Callout> : null}

        <div className="form-section-title" style={{ marginTop: 8 }}>מפתחות Sandbox (סביבת בדיקות)</div>
        {s.sandbox.connected ? (
          <div className="row wrap">
            <span className="badge ok">מחובר</span>
            <bdi className="ltr small">{s.sandbox.apiKeyId}</bdi>
            <span className="spacer" />
            <Button variant="ghost" size="sm" disabled={!isAdmin} loading={busy === 'sb-off'} onClick={() => run('sb-off', () => api('/api/integrations/morning/sandbox', { method: 'DELETE' }), 'מפתחות ה-Sandbox הוסרו')}>הסרה</Button>
          </div>
        ) : (
          <>
            <p className="small muted" style={{ margin: 0 }}>
              חשבון Sandbox נפרד נפתח באתר המפתחים של מורנינג. המפתחות האלה משמשים רק להפקת מסמכי בדיקה — החיבור הראשי (שממנו סוכן מורנינג קורא) לא משתנה.
            </p>
            <div className="form-grid">
              <Field label="Sandbox Client ID">
                <Input value={apiKeyId} onChange={(e) => setApiKeyId(e.target.value)} ltr autoComplete="off" />
              </Field>
              <Field label="Sandbox Client Secret">
                <Input type="password" value={secret} onChange={(e) => setSecret(e.target.value)} ltr autoComplete="new-password" />
              </Field>
            </div>
            <div className="row">
              <Button
                onClick={() => run('sb', () => api('/api/integrations/morning/sandbox', { body: { apiKeyId: apiKeyId.trim(), secret: secret.trim() } }), 'ה-Sandbox חובר — הפקה לבדיקות פעילה')}
                loading={busy === 'sb'}
                disabled={!isAdmin || apiKeyId.trim().length < 8 || secret.trim().length < 8}
              >
                חיבור Sandbox ובדיקה
              </Button>
              {!isAdmin ? <span className="small muted">למנהל בלבד</span> : null}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function Rules() {
  const { data, error } = useSettings();
  const [s, setS] = useState<Settings | null>(null);
  const [senders, setSenders] = useState('');
  const [keywords, setKeywords] = useState('');
  const [issuers, setIssuers] = useState('');
  const [quoteOnly, setQuoteOnly] = useState('');
  const [busy, setBusy] = useState(false);
  const toast = useToast();
  const qc = useQueryClient();

  useEffect(() => {
    if (!data) return;
    setS(data.settings);
    setSenders(data.settings.allowedSenders.join('\n'));
    setKeywords(data.settings.documentKeywords.join('\n'));
    setIssuers(data.settings.issuerCompanyNumbers.join('\n'));
    setQuoteOnly(data.settings.quoteOnlyIssuers.join('\n'));
  }, [data]);

  if (error) return <div className="panel"><ErrorState error={error} /></div>;
  if (!s) return <div className="panel panel-body"><span className="skeleton" style={{ width: '40%' }} /></div>;

  const issuerList = lines(issuers);
  const badIssuer = issuerList.find((x) => !/^\d{5,9}$/.test(x));
  const quoteOnlyList = lines(quoteOnly);
  const badQuoteOnly = quoteOnlyList.find((x) => !/^\d{5,9}$/.test(x));
  const senderList = lines(senders).map((x) => x.toLowerCase());
  const badSender = senderList.find((x) => !/^(@[\w.-]+\.\w+|[^\s@]+@[\w.-]+\.\w+)$/.test(x));

  const save = async () => {
    setBusy(true);
    try {
      await api('/api/settings', { method: 'PUT', body: { ...s, allowedSenders: senderList, documentKeywords: lines(keywords), issuerCompanyNumbers: issuerList, quoteOnlyIssuers: quoteOnlyList } });
      qc.invalidateQueries({ queryKey: ['settings'] });
      toast.success(t.common.savedToast);
    } catch (e) {
      toast.error(e);
    } finally {
      setBusy(false);
    }
  };

  const toggleType = (type: DocumentType, on: boolean) => setS({ ...s, importTypes: on ? [...s.importTypes, type] : s.importTypes.filter((x) => x !== type) });

  return (
    <div className="grid-main-side">
      <div className="panel panel-body">
        <div className="form-section">
          <div className="form-section-title">מקור המסמכים</div>
          <div className="form-grid">
            <Field label="שולחים מורשים" hint="כתובת מלאה או דומיין שמתחיל ב-@, שורה לכל אחד" error={badSender ? `כתובת לא תקינה: ${badSender}` : null}>
              <Textarea value={senders} onChange={(e) => setSenders(e.target.value)} dir="ltr" style={{ textAlign: 'right', minHeight: 96 }} />
            </Field>
            <Field label="מילות מפתח" hint="לפחות אחת חייבת להופיע בנושא או בגוף המייל">
              <Textarea value={keywords} onChange={(e) => setKeywords(e.target.value)} style={{ minHeight: 96 }} />
            </Field>
            <Field label="מספרי העוסק / ח.פ של העסק שלך" hint="מסמכים שהונפקו על ידי עסק אחר (למשל חשבונית מספק אליך) יסוננו" error={badIssuer ? `מספר לא תקין: ${badIssuer}` : null} className="span-2">
              <Textarea value={issuers} onChange={(e) => setIssuers(e.target.value)} dir="ltr" style={{ textAlign: 'right', minHeight: 64 }} />
            </Field>
            <Field label="חשבונות שמהם נקלטות רק הצעות מחיר" hint="למשל החשבון הישן MARKIT — חשבוניות, קבלות וזיכויים ממנו מסוננים; הכל נלקח מהחשבון הראשי" error={badQuoteOnly ? `מספר לא תקין: ${badQuoteOnly}` : null} className="span-2">
              <Textarea value={quoteOnly} onChange={(e) => setQuoteOnly(e.target.value)} dir="ltr" style={{ textAlign: 'right', minHeight: 48 }} />
            </Field>
          </div>
        </div>

        <div className="form-section">
          <div className="form-section-title">סוגי מסמכים לייבוא</div>
          <div className="row wrap" style={{ gap: 18 }}>
            {(['tax_invoice', 'tax_invoice_receipt', 'receipt', 'credit_note', 'delivery_note', 'quote', 'proforma'] as DocumentType[]).map((d) => (
              <Checkbox key={d} checked={s.importTypes.includes(d)} onChange={(v) => toggleType(d, v)}>{t.enums.docType[d]}</Checkbox>
            ))}
          </div>
        </div>

        <div className="form-section">
          <div className="form-section-title">שיוך אוטומטי</div>
          <div className="form-grid">
            <Field label="סף ביטחון לשיוך אוטומטי" hint="מתחת לסף — המסמך ימתין לבדיקה שלך. מומלץ: 90%">
              <Input type="number" min={80} max={99} value={Math.round(s.autoMatchThreshold * 100)} onChange={(e) => setS({ ...s, autoMatchThreshold: Math.min(0.99, Math.max(0.8, Number(e.target.value) / 100)) })} ltr />
            </Field>
            <Field label="תדירות בדיקת המייל (דקות)">
              <Input type="number" min={1} max={60} value={s.pollIntervalMinutes} onChange={(e) => setS({ ...s, pollIntervalMinutes: Math.min(60, Math.max(1, Number(e.target.value) || 5)) })} ltr />
            </Field>
            <Field label="בחיבור ראשון — ייבוא מסמכים מהימים האחרונים" hint="ניתן לייבא היסטוריה ארוכה יותר ממסך סוכן המייל">
              <Input type="number" min={0} max={90} value={s.initialSyncDays} onChange={(e) => setS({ ...s, initialSyncDays: Math.min(90, Math.max(0, Number(e.target.value) || 0)) })} ltr />
            </Field>
          </div>
        </div>

        <div className="row">
          <Button variant="primary" onClick={save} loading={busy} disabled={Boolean(badIssuer || badSender || badQuoteOnly) || s.importTypes.length === 0 || lines(keywords).length === 0}>
            {t.common.save}
          </Button>
          {data ? (
            <Button
              variant="ghost"
              onClick={() => {
                setS(data.defaults);
                setSenders(data.defaults.allowedSenders.join('\n'));
                setKeywords(data.defaults.documentKeywords.join('\n'));
                setIssuers(data.defaults.issuerCompanyNumbers.join('\n'));
                setQuoteOnly(data.defaults.quoteOnlyIssuers.join('\n'));
              }}
            >
              שחזור ברירת מחדל
            </Button>
          ) : null}
        </div>
      </div>
      <aside className="stack">
        <Callout tone="info" title="איך הסוכן מחליט">
          <ol style={{ margin: '6px 0 0', paddingInlineStart: 18 }} className="small">
            <li>מייל משולח מורשה, עם מילת מפתח.</li>
            <li>קובץ PDF שבו מזוהים סוג ומספר מסמך.</li>
            <li>המסמך הונפק על ידי העסק שלך.</li>
            <li>המסמך לא קיים כבר במערכת.</li>
            <li>שיוך ללקוח לפי ח.פ, ואם אין — לפי השם במסמך ושמות שנלמדו.</li>
            <li>מתחת לסף הביטחון — המסמך ממתין לבדיקה שלך.</li>
          </ol>
        </Callout>
      </aside>
    </div>
  );
}

function Account() {
  const { user } = useAuth();
  const toast = useToast();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [touched, setTouched] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const errors = {
    current: current ? null : 'יש להזין את הסיסמה הנוכחית',
    next: next.length >= 10 ? (next === current ? 'הסיסמה החדשה זהה לנוכחית' : null) : 'לפחות 10 תווים',
    confirm: confirm === next ? null : 'הסיסמאות אינן תואמות',
  };
  const valid = !errors.current && !errors.next && !errors.confirm;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setTouched(true);
    setError(null);
    if (!valid) return;
    setBusy(true);
    try {
      await api('/api/auth/password', { body: { currentPassword: current, newPassword: next } });
      toast.success('הסיסמה עודכנה. התחברויות במכשירים אחרים נותקו.');
      setCurrent('');
      setNext('');
      setConfirm('');
      setTouched(false);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t.common.unknownError);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid-main-side">
      <form className="panel panel-body stack" onSubmit={submit} noValidate style={{ maxWidth: 520 }}>
        <div>
          <div className="form-section-title" style={{ marginBottom: 4 }}>שינוי סיסמה</div>
          <div className="small muted">
            מחובר כ־<bdi>{user?.email}</bdi>
          </div>
        </div>
        {error ? <Callout tone="bad">{error}</Callout> : null}
        <Field label="סיסמה נוכחית" htmlFor="pw-current" error={touched ? errors.current : null}>
          <Input id="pw-current" type="password" autoComplete="current-password" ltr value={current} onChange={(e) => setCurrent(e.target.value)} />
        </Field>
        <Field label="סיסמה חדשה" htmlFor="pw-new" hint="לפחות 10 תווים" error={touched ? errors.next : null}>
          <Input id="pw-new" type="password" autoComplete="new-password" ltr value={next} onChange={(e) => setNext(e.target.value)} />
        </Field>
        <Field label="אימות סיסמה חדשה" htmlFor="pw-confirm" error={touched ? errors.confirm : null}>
          <Input id="pw-confirm" type="password" autoComplete="new-password" ltr value={confirm} onChange={(e) => setConfirm(e.target.value)} />
        </Field>
        <div>
          <Button type="submit" variant="primary" loading={busy}>עדכון סיסמה</Button>
        </div>
      </form>
      <aside>
        <Callout tone="info" title="אבטחה">
          <span className="small">הסיסמה נשמרת מוצפנת (scrypt) ואי אפשר לשחזר אותה — רק להחליף. אחרי שינוי, כל ההתחברויות האחרות מתנתקות.</span>
        </Callout>
      </aside>
    </div>
  );
}

function Backups() {
  const toast = useToast();
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const { data, error, isLoading } = useQuery({
    queryKey: ['backups'],
    queryFn: () =>
      api<{
        dir: string;
        items: Array<{ name: string; bytes: number; createdAt: string }>;
        offsite: { ok: boolean; finishedAt: string; latestDatabaseBackup: string; databaseBackups: number; files: number; errors: string } | null;
      }>('/api/backups'),
  });
  const now = async () => {
    setBusy(true);
    try {
      const r = await api<{ file: string; rows: number }>('/api/backups', { method: 'POST' });
      toast.success(`גיבוי נשמר (${r.rows.toLocaleString('he-IL')} רשומות)`);
      qc.invalidateQueries({ queryKey: ['backups'] });
    } catch (e) {
      toast.error(e);
    } finally {
      setBusy(false);
    }
  };
  if (error) return <div className="panel"><ErrorState error={error} /></div>;
  const last = data?.items[0];
  const kind = (n: string) => (n.includes('-daily') ? 'אוטומטי יומי' : n.includes('-manual') ? 'ידני' : 'אחר');
  return (
    <div className="grid-main-side">
      <section className="panel">
        <div className="panel-head">
          <h2>גיבויי מסד הנתונים</h2>
          <span className="spacer" />
          <Button variant="primary" onClick={now} loading={busy}>גיבוי עכשיו</Button>
        </div>
        {isLoading ? (
          <div className="panel-body"><span className="skeleton" style={{ width: '40%' }} /></div>
        ) : !data?.items.length ? (
          <div className="panel-body muted">עדיין אין גיבויים. הגיבוי האוטומטי הראשון נוצר כשסוכן המייל פועל.</div>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>נוצר</th>
                <th>סוג</th>
                <th className="num">גודל</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {data.items.map((b) => (
                <tr key={b.name}>
                  <td className="tabular">{fmtDateTime(b.createdAt)}</td>
                  <td>{kind(b.name)}</td>
                  <td className="num">{(b.bytes / 1024).toFixed(0)} KB</td>
                  <td className="num">
                    <a className="btn sm" href={`/api/backups/${encodeURIComponent(b.name)}`}>הורדה</a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
      <aside className="stack">
        <Callout tone={last ? 'ok' : 'warn'} title={last ? `גיבוי אחרון: ${fmtDateTime(last.createdAt)}` : 'אין גיבוי'}>
          <span className="small">
            כל הנתונים נשמרים מיד בכל פעולה. בנוסף, סוכן המייל יוצר גיבוי מלא פעם ביום ושומר את 30 האחרונים.
          </span>
        </Callout>
        {data?.offsite ? (
          <Callout
            tone={data.offsite.ok && Date.now() - Date.parse(data.offsite.finishedAt) < 36 * 3_600_000 ? 'ok' : 'warn'}
            title={`גיבוי ל-Google Drive: ${fmtDateTime(data.offsite.finishedAt)}`}
          >
            <span className="small">
              {data.offsite.ok ? 'הצליח' : `נכשל (${data.offsite.errors})`} · {data.offsite.databaseBackups} גיבויי מסד נתונים ו-{data.offsite.files.toLocaleString('he-IL')} קבצי מסמכים
              בתיקייה "Markit CRM Backup" ב-Google Drive. מתעדכן כל לילה ב-03:30.
            </span>
          </Callout>
        ) : (
          <Callout tone="info" title="מומלץ">
            <span className="small">
              להוריד מדי פעם גיבוי ולשמור אותו מחוץ לשרת. תיקיית הגיבויים: <bdi className="ltr">{data?.dir}</bdi>
            </span>
          </Callout>
        )}
      </aside>
    </div>
  );
}

function Audit() {
  const { data, error, isLoading } = useQuery({
    queryKey: ['audit'],
    queryFn: () => api<{ items: Array<{ id: number; action: string; entityType: string | null; entityId: string | null; ip: string | null; createdAt: string; userName: string | null }> }>('/api/audit?limit=200'),
  });
  const LABELS: Record<string, string> = {
    login: 'התחברות',
    login_failed: 'ניסיון התחברות שנכשל',
    'customer.create': 'יצירת לקוח',
    'customer.update': 'עדכון לקוח',
    'customer.delete': 'מחיקת לקוח',
    'customers.import': 'ייבוא לקוחות',
    'document.update': 'עדכון מסמך',
    'document.delete': 'מחיקת מסמך',
    'allocation.delete': 'הסרת שיוך תשלום',
    'ingestion.resolve': 'שיוך מסמך מתור הבדיקה',
    'gmail.connect': 'חיבור Gmail',
    'gmail.disconnect': 'ניתוק Gmail',
    'agent.backfill': 'ייבוא היסטוריה',
    'settings.update': 'שינוי הגדרות',
    password_changed: 'שינוי סיסמה',
    'backup.create': 'יצירת גיבוי',
    'backup.download': 'הורדת גיבוי',
    password_change_failed: 'ניסיון שינוי סיסמה שנכשל',
  };
  if (error) return <div className="panel"><ErrorState error={error} /></div>;
  return (
    <section className="panel">
      <table className="table">
        <thead>
          <tr>
            <th>זמן</th>
            <th>משתמש</th>
            <th>פעולה</th>
            <th className="hide-sm">רשומה</th>
            <th className="hide-sm">IP</th>
          </tr>
        </thead>
        <tbody>
          {isLoading ? (
            <tr><td colSpan={5}><span className="skeleton" style={{ width: '40%' }} /></td></tr>
          ) : (
            data?.items.map((a) => (
              <tr key={a.id}>
                <td className="tabular nowrap small">{fmtDateTime(a.createdAt)}</td>
                <td>{a.userName ?? <span className="faint">—</span>}</td>
                <td style={a.action.endsWith('failed') ? { color: 'var(--bad)' } : undefined}>{LABELS[a.action] ?? a.action}</td>
                <td className="hide-sm muted small">{a.entityType ? `${a.entityType} #${a.entityId}` : ''}</td>
                <td className="hide-sm muted small"><bdi>{a.ip}</bdi></td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </section>
  );
}
